import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { getBaseUrl } from '../../Utils'

interface AttrDetail {
    MetadataId: number
    ImplicitFilter: boolean
}

interface RoleAttribute {
    dTO_RoleAttributes: {
        AttrDetails: AttrDetail[]
    }
    AttributeDescription: string[]
    AttributeName: string[]
    roleSecCount: number
}

interface RoleDashBoards {
    dTO_RoleDashBoardsDetails: {
        DashBoards: any[]
    }
    roleDashBoardsCount: number
}

interface RoleSecurityDetails {
    roleSecCount: number
    FeaturesSecurity: any[]
}

interface RoleAttributesAdd {
    roleSecurities: {
        RoleAttributes: {
            MetaDataId: number
            FieldName: string
        }[]
        attributeList?: string[]
    }
}

interface FilteredAttributes {
    attributeList: {
        MetaDataId: number
        FieldName: string
    }[]
}

interface AssignAttributesToRole {
    roleId: number
    attrKeys: number[]
}

interface RoleSecurityCode {
    SecCode: string
    SecDescription: string
    SecEnum: number
    SecHelpText: string
    Available: boolean
    Restricted: boolean
}

interface GetRoleSecurities {
    roleId: number
    roleSecurities: {
        RoleSecurityCodes: RoleSecurityCode[]
    }
}

interface FilteredSecurities {
    SecCode: string
    SecDescription: string
    SecEnum: number
    SecHelpText: string
    Available: boolean
    Restricted: boolean
}

interface GetFilteredSecurities {
    roleId: number
    securityList: FilteredSecurities[]
}

interface RoleDashBoardsToAdd {
    CreatedByUserId: number
    CreatedOn: Date
    DashBoardGuid: string
    DashBoardId: number
    Description: string
    LastUpdatedByUserId: number
    LastUpdatedOn: Date
    Name: string
    SharedRolesCount: number
    SharedUsersCount: number
    numberofVU: number
    profilePic: string
    rgborname_color: string
}

interface AssignSecurityCodesToRole {
    roleId: number
    secKeys: number[]
}
interface GetRoleDashBoardsToAdd {
    roleId: number
    dashBoards: RoleDashBoardsToAdd[]
}
interface AddRoleDashBoard {
    roleId: number
    dashBoardId: number
}
interface DeleteAttribute {
    roleId: number
    pID: number
}
interface DeleteWorkspace {
    roleId: number
    dashBoardId: number
}
interface DeletePrivilege {
    roleId: number
    pID: number
}

interface UpdateRoleAttribute {
    IsImplictFilter: boolean
    pId: number
    roleId: string
}
interface ItemState {
    roles: any[]
    roleAttributes: RoleAttribute
    roledashBoards: RoleDashBoards
    roleSecurityDetails: RoleSecurityDetails
    roleattributesAdd: RoleAttributesAdd
    getfilteredattributes: FilteredAttributes
    assignAttributesToRole: AssignAttributesToRole
    getroleSecurities: GetRoleSecurities
    getfilteredsecurities: GetFilteredSecurities
    assignSecurityCodesToRole: AssignSecurityCodesToRole
    getRoleDashBoardsToAdd: GetRoleDashBoardsToAdd
    addRoleDashBoard: AddRoleDashBoard
    deleteAttribute: DeleteAttribute
    deleteWorkspace: DeleteWorkspace
    deletePrivilege: DeletePrivilege
    updateRoleAttribute: UpdateRoleAttribute
    status: 'idle' | 'loading' | 'succeeded' | 'failed'
    error: string | null
}

const initialState: ItemState = {
    roles: [],
    roleAttributes: {
        dTO_RoleAttributes: {
            AttrDetails: [],
        },
        AttributeDescription: [],
        AttributeName: [],
        roleSecCount: 0,
    },
    roledashBoards: {
        dTO_RoleDashBoardsDetails: {
            DashBoards: [],
        },
        roleDashBoardsCount: 0,
    },
    roleSecurityDetails: {
        FeaturesSecurity: [],
        roleSecCount: 0,
    },
    roleattributesAdd: {
        roleSecurities: {
            RoleAttributes: [],
        },
    },
    getfilteredattributes: {
        attributeList: [],
    },
    assignAttributesToRole: {
        roleId: 0,
        attrKeys: [],
    },
    getroleSecurities: {
        roleId: 0,
        roleSecurities: {
            RoleSecurityCodes: [],
        },
    },
    getfilteredsecurities: {
        roleId: 0,
        securityList: [],
    },
    assignSecurityCodesToRole: {
        roleId: 0,
        secKeys: [],
    },
    getRoleDashBoardsToAdd: {
        roleId: 0,
        dashBoards: [],
    },
    addRoleDashBoard: {
        roleId: 0,
        dashBoardId: 0,
    },
    deleteAttribute: {
        roleId: 0,
        pID: 0,
    },
    deleteWorkspace: {
        roleId: 0,
        dashBoardId: 0,
    },
    deletePrivilege: {
        roleId: 0,
        pID: 0,
    },
    updateRoleAttribute: {
        IsImplictFilter: false,
        pId: 0,
        roleId: '',
    },
    status: 'idle',
    error: null,
}

async function GetRoles() {
    const response = await axios.get(`${getBaseUrl()}/API/v3/Roles`)
    return response.data.data
}

async function GetRoleAttribute(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleAttribute_Details`, {
        roleId,
    })
    return response.data.data
}

async function GetRoleDashBoards(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleDashBoards`, {
        roleId,
    })
    return response.data.data
}

async function GetRoleSecurity_Details(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleSecurity_Details`, {
        roleId,
    })
    return response.data.data
}

async function GetRoleAttributesAdd(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleAttributes`, {
        roleId,
    })
    return response.data.data
}

async function GetFilteredAttributes(roleId: number, searchstring: string) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetFilteredAttributes`, {
        roleId,
        searchstring,
    })
    return response.data.data
}

async function GetAssignAttributesToRole(roleId: number, attrKeys: number[]) {
    const response = await axios.put(`${getBaseUrl()}/API/v3/Roles/AssignAttributesToRole`, {
        roleId,
        attrKeys,
    })
    return response.data.data
}

async function RoleSecurity(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleSecurity`, {
        roleId,
    })
    return response.data.data
}

async function filteredSecurities(roleId: number, searchString: string) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetFilteredSecurities`, {
        roleId,
        searchString,
    })
    return response.data.data
}

async function GetAssignSecurityCodesToRole(roleId: number, secKeys: number[]) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/AssignSecurityCodesToRole`, {
        roleId,
        secKeys,
    })
    return response.data.data
}

async function RoleDashBoardsToAdd(roleId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/GetRoleDashBoardsToAdd`, {
        roleId,
    })
    return response.data.data
}

async function AddRoleDashBoard(roleId: number, dashBoardId: number) {
    const response = await axios.post(`${getBaseUrl()}/API/v3/Roles/AddRoleDashBoard`, {
        roleId,
        dashBoardId,
    })
    return response.data.data
}

async function DeleteAttribute(roleId: number, pID: number) {
    try {
        const url = `${getBaseUrl()}/API/v3/Roles/DeleteAttribute`
        const response = await axios.delete(url, {
            data: { roleId, pID },
        })
        return response.data
    } catch (error) {
        console.error('Error occurred while deleting attribute:', error)
        throw error
    }
}

async function DeleteRoleDashBoard(roleId: number, dashBoardId: number) {
    try {
        const url = `${getBaseUrl()}/API/v3/Roles/DeleteRoleDashBoard`
        const response = await axios.delete(url, {
            data: { roleId, dashBoardId },
        })
        return response.data
    } catch (error) {
        console.error('Error occurred while deleting privilege:', error)
        throw error
    }
}

async function DeletePrivilege(roleId: number, pID: number) {
    try {
        const url = `${getBaseUrl()}/API/v3/Roles/DeletePrivilege`
        const response = await axios.delete(url, {
            data: { roleId, pID },
        })
        return response.data
    } catch (error) {
        console.error('Error occurred while deleting privilege:', error)
        throw error
    }
}

async function UpdateRoleAttribute(roleId: string, pId: number, IsImplictFilter: boolean) {
    const response = await axios.put(`${getBaseUrl()}/API/v3/Roles/UpdateRoleAttribute`, {
        roleId,
        pId,
        IsImplictFilter,
    })
    return response.data.data
}

export const Roles = createAsyncThunk('items/getRoles', GetRoles)

export const RoleAttribute = createAsyncThunk('items/getRoleAttribute', async (roleId: number) => {
    const response = await GetRoleAttribute(roleId)
    return response
})

export const RoleDashBoards = createAsyncThunk(
    'items/getRoleDashBoards',
    async (roleId: number) => {
        const response = await GetRoleDashBoards(roleId)
        return response
    }
)

export const RoleSecurityDetails = createAsyncThunk(
    'items/getGetRoleSecurity_Details',
    async (roleId: number) => {
        const response = await GetRoleSecurity_Details(roleId)
        return response
    }
)

export const RoleAttributesAdd = createAsyncThunk(
    'items/getRoleAttributesAdd',
    async (roleId: number) => {
        const response = await GetRoleAttributesAdd(roleId)
        return response
    }
)

export const FilteredAttributes = createAsyncThunk(
    'items/getFilteredAttributes',
    async ({ roleId, searchstring }: { roleId: number; searchstring: string }) => {
        const response = await GetFilteredAttributes(roleId, searchstring)
        return response
    }
)

export const AssignAttributesToRole = createAsyncThunk(
    'items/getAssignAttributesToRole',
    async ({ roleId, attrKeys }: { roleId: number; attrKeys: number[] }) => {
        const response = await GetAssignAttributesToRole(roleId, attrKeys)
        return response
    }
)

export const GetRoleSecurity = createAsyncThunk('items/getRoleSecurity', async (roleId: number) => {
    const response = await RoleSecurity(roleId)
    return response
})

export const GetFilteredSecurities = createAsyncThunk(
    'items/getFilteredSecurities',
    async ({ roleId, searchString }: { roleId: number; searchString: string }) => {
        const response = await filteredSecurities(roleId, searchString)
        return response
    }
)

export const AssignSecurityCodesToRole = createAsyncThunk(
    'items/getAssignSecurityCodesToRole',
    async ({ roleId, secKeys }: { roleId: number; secKeys: number[] }) => {
        const response = await GetAssignSecurityCodesToRole(roleId, secKeys)
        return response
    }
)

export const GetRoleDashBoardsToAdd = createAsyncThunk(
    'items/getRoleDashBoardsToAdd',
    async (roleId: number) => {
        const response = await RoleDashBoardsToAdd(roleId)
        return response
    }
)

export const GetAddRoleDashBoard = createAsyncThunk(
    'items/getAddRoleDashBoard',
    async ({ roleId, dashBoardId }: { roleId: number; dashBoardId: number }) => {
        const response = await AddRoleDashBoard(roleId, dashBoardId)
        return response
    }
)

export const GetDeleteAttribute = createAsyncThunk(
    'items/getDeleteAttribute',
    async ({ roleId, pID }: { roleId: number; pID: number }) => {
        const response = await DeleteAttribute(roleId, pID)
        return response
    }
)

export const GetDeleteRoleDashBoard = createAsyncThunk(
    'items/getDeleteRoleDashBoard',
    async ({ roleId, dashBoardId }: { roleId: number; dashBoardId: number }) => {
        const response = await DeleteRoleDashBoard(roleId, dashBoardId)
        return response
    }
)

export const GetDeletePrivilege = createAsyncThunk(
    'items/getDeletePrivilege',
    async ({ roleId, pID }: { roleId: number; pID: number }) => {
        const response = await DeletePrivilege(roleId, pID)
        return response
    }
)

export const GetUpdateRoleAttribute = createAsyncThunk(
    'items/getUpdateRoleAttribute',
    async ({
        roleId,
        pId,
        IsImplictFilter,
    }: {
        roleId: string
        pId: number
        IsImplictFilter: boolean
    }) => {
        const response = await UpdateRoleAttribute(roleId, pId, IsImplictFilter)
        return response
    }
)

const itemSlice = createSlice({
    name: 'items',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(Roles.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(Roles.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.roles = action.payload
            })
            .addCase(Roles.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch roles'
            })
            .addCase(RoleAttribute.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(RoleAttribute.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.roleAttributes = action.payload
            })
            .addCase(RoleAttribute.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role attributes'
            })
            .addCase(RoleDashBoards.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(RoleDashBoards.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.roledashBoards = action.payload
            })
            .addCase(RoleDashBoards.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role dashboards'
            })
            .addCase(RoleSecurityDetails.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(RoleSecurityDetails.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.roleSecurityDetails = action.payload
            })
            .addCase(RoleSecurityDetails.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role Security Details'
            })
            .addCase(RoleAttributesAdd.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(RoleAttributesAdd.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.roleattributesAdd = action.payload
            })
            .addCase(RoleAttributesAdd.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role attributes add'
            })
            .addCase(FilteredAttributes.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(FilteredAttributes.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.getfilteredattributes = action.payload
            })
            .addCase(FilteredAttributes.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch filtered attributes'
            })
            .addCase(AssignAttributesToRole.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(AssignAttributesToRole.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.assignAttributesToRole = action.payload
            })
            .addCase(AssignAttributesToRole.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to assign attributes to role'
            })
            .addCase(GetRoleSecurity.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetRoleSecurity.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.getroleSecurities = action.payload
            })
            .addCase(GetRoleSecurity.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role securities'
            })
            .addCase(GetFilteredSecurities.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetFilteredSecurities.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.getfilteredsecurities = action.payload
            })
            .addCase(GetFilteredSecurities.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch filtered securities'
            })
            .addCase(AssignSecurityCodesToRole.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(AssignSecurityCodesToRole.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.assignSecurityCodesToRole = action.payload
            })
            .addCase(AssignSecurityCodesToRole.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch roles'
            })
            .addCase(GetRoleDashBoardsToAdd.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetRoleDashBoardsToAdd.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.getRoleDashBoardsToAdd = action.payload
            })
            .addCase(GetRoleDashBoardsToAdd.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to fetch role dashboards to add'
            })
            .addCase(GetAddRoleDashBoard.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetAddRoleDashBoard.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.addRoleDashBoard = action.payload
            })
            .addCase(GetAddRoleDashBoard.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to add role dashboard'
            })
            .addCase(GetDeleteAttribute.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetDeleteAttribute.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.deleteAttribute = action.payload
            })
            .addCase(GetDeleteAttribute.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to add role dashboard'
            })
            .addCase(GetDeletePrivilege.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetDeletePrivilege.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.deletePrivilege = action.payload
            })
            .addCase(GetDeletePrivilege.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to add role dashboard'
            })
            .addCase(GetDeleteRoleDashBoard.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetDeleteRoleDashBoard.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.deleteWorkspace = action.payload
            })
            .addCase(GetDeleteRoleDashBoard.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to add role dashboard'
            })
            .addCase(GetUpdateRoleAttribute.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(GetUpdateRoleAttribute.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.updateRoleAttribute = action.payload
            })
            .addCase(GetUpdateRoleAttribute.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.error.message || 'Failed to Update role attribute'
            })
    },
})

export default itemSlice.reducer
